import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@api/clients';
import { storeFeatures } from '@core';
import { type TotpConfirmResult, type TotpEnrollment } from '@models';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { AuthActions } from '@ngrx-store';
import { NotificationsService } from '@ui';

interface TotpAuthState {
  currentCodeVerified: boolean;
  otpauthUrl: string | null;
  recoveryCodes: string[] | null;
  secret: string | null;
}

const initialState: TotpAuthState = {
  currentCodeVerified: false,
  otpauthUrl: null,
  recoveryCodes: null,
  secret: null,
};

export const TotpAuthStore = signalStore(
  { providedIn: 'root' },
  withState<TotpAuthState>(initialState),
  storeFeatures.withRequestStatus(),
  withMethods((
    store,
    authService = inject(AuthService),
    dialog = inject(MatDialog),
    ngrxStore = inject(Store),
    notificationService = inject(NotificationsService),
  ) => ({
    clearError() {
      patchState(store, { requestStatus: 'idle' });
    },

    completeEnrollment() {
      patchState(store, initialState);
      dialog.closeAll();
      notificationService.showSuccessMessage('Two-factor authentication enabled');
    },

    confirm(token: string) {
      store.setPending();

      authService.confirmTotp<TotpConfirmResult>({ totp: token }).subscribe({
        error: error => store.setError(error.message),
        next: result => {
          store.setFulfilled();
          patchState(store, { recoveryCodes: result.recoveryCodes });
          ngrxStore.dispatch(AuthActions.refreshProfile());
        },
      });
    },

    enroll() {
      store.setPending();

      authService.enrollTotp<TotpEnrollment>().subscribe({
        error: error => store.setError(error.message),
        next: enrollment => {
          store.setFulfilled();
          patchState(store, { otpauthUrl: enrollment.otpauthUrl, secret: enrollment.secret });
        },
      });
    },

    removeApp(token: string) {
      store.setPending();

      authService.removeTotpApp({ totp: token }).subscribe({
        error: error => store.setError(error.message),
        next: () => {
          store.setFulfilled();
          patchState(store, initialState);
          dialog.closeAll();
          ngrxStore.dispatch(AuthActions.refreshProfile());
          notificationService.showSuccessMessage('Authenticator app removed');
        },
      });
    },

    reset() {
      patchState(store, { ...initialState, requestStatus: 'idle' });
    },

    verifyCurrent(token: string) {
      patchState(store, { currentCodeVerified: false });
      store.setPending();

      authService.verifyCurrentTotp({ totp: token }).subscribe({
        error: error => store.setError(error.message),
        next: () => {
          store.setFulfilled();
          patchState(store, { currentCodeVerified: true });
        },
      });
    },
  })),
);
